import { inject } from '@angular/core';
import type {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
} from '@angular/router';
import { Auth } from '@freelancer/auth';
import { Datastore } from '@freelancer/datastore';
import type { MembershipSubscriptionHistoryCollection } from '@freelancer/datastore/collections';
import { Location } from '@freelancer/location';
import { SubscriptionStatusApi } from 'api-typings/memberships/memberships_types';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export const FreightlancerMembershipPageGuard = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): ReturnType<CanActivateFn> => {
  const auth = inject(Auth);
  const datastore = inject(Datastore);
  const location = inject(Location);

  return auth.isLoggedIn().pipe(
    switchMap(isLoggedIn => {
      if (isLoggedIn) {
        const loadshiftMemberships = [
          'loadshift_membership',
          'loadshift_membership_migrated',
          'loadshift_membership_free_trial',
          'loadshift_membership_v1',
          'loadshift_membership_free_trial_v1',
          'loadshift_v2',
          'loadshift_free_trial_v2',
          'loadshift_v3',
        ];

        return datastore
          .collection<MembershipSubscriptionHistoryCollection>(
            'membershipSubscriptionHistory',
            query =>
              query.where('status', 'in', [
                SubscriptionStatusApi.PENDING,
                SubscriptionStatusApi.ACTIVE,
              ]),
          )
          .valueChanges()
          .pipe(
            map(subscriptions =>
              subscriptions
                .map(b => {
                  return b.package.internalName;
                })
                .some(subscription =>
                  loadshiftMemberships.includes(subscription),
                ),
            ),
            map(hasLoadShiftMembership => {
              return hasLoadShiftMembership
                ? location.createUrlTree(['user/settings/membership'])
                : true;
            }),
          );
      }
      return of(true);
    }),
  );
};
